import React from 'react';

import { Alert, Checkbox, DatePicker, Flex, message, Modal, Select, Space, Typography } from 'antd';
import dayjs from 'dayjs';
import { useTranslation } from 'react-i18next';

import { NSKeys } from '@app/i18n';
import {
  CreateAssignmentSchema,
  ListRolesSchema,
  RetrieveAssignmentSchema,
  RetrieveUserSchema,
  UpdateAssignmentSchema,
} from '@app/models/users';

import { useCreateAssignmentMutation, useUpdateAssignmentMutation } from '@pages/Employees/hooks';
import { VisibilityState } from '@pages/Employees/interfaces';
import { useRoleModuleCaption } from '@pages/SpaceSettings/hooks';

import { RolePermissionsPreview } from './components/RolePermissionsPreview';
import { VisibilityRuleBuilder } from './components/VisibilityRuleBuilder';
import {
  BASELINE_ROLE_CODE,
  buildVisibilityRule,
  hasAnyMessage,
  parseAssignmentError,
  ParsedAssignmentError,
  parseVisibilityRule,
} from './services';
const EMPTY_PARSED: ParsedAssignmentError = {
  fieldErrors: {},
  ruleErrors: [],
  generalErrors: [],
  isSelfForbidden: false,
};
interface IProps {
  open: boolean;
  onClose: () => void;
  employee: RetrieveUserSchema;
  assignment?: RetrieveAssignmentSchema;
  roles: ListRolesSchema[];
  rolesLoading: boolean;
  ruleAttributes: string[];
}
/** Модальное окно создания / редактирования назначения. */
export const AssignmentModal: React.FC<IProps> = ({
  open,
  onClose,
  employee,
  assignment,
  roles,
  rolesLoading,
  ruleAttributes,
}) => {
  const { t } = useTranslation(NSKeys.assignments);
  const commonT = useTranslation(NSKeys.common);
  const validationT = useTranslation(NSKeys.validation);
  const isEdit = !!assignment;
  const [roleId, setRoleId] = React.useState<number | undefined>(undefined);
  const [indefinite, setIndefinite] = React.useState(true);
  const [validFrom, setValidFrom] = React.useState<string | null>(null);
  const [validTo, setValidTo] = React.useState<string | null>(null);
  const [visibility, setVisibility] = React.useState<VisibilityState>({});
  const [visibilityTouched, setVisibilityTouched] = React.useState(false);
  const [parsed, setParsed] = React.useState<ParsedAssignmentError>(EMPTY_PARSED);
  const create = useCreateAssignmentMutation();
  const update = useUpdateAssignmentMutation();
  const roleModuleCaption = useRoleModuleCaption();
  const submitting = create.isLoading || update.isLoading;
  // Инициализация при открытии.
  React.useEffect(() => {
    if (!open) {
      return;
    }
    setParsed(EMPTY_PARSED);
    setVisibilityTouched(false);
    if (assignment) {
      setRoleId(assignment.role_id);
      setIndefinite(!assignment.valid_from && !assignment.valid_to);
      setValidFrom(assignment.valid_from);
      setValidTo(assignment.valid_to);
      setVisibility(parseVisibilityRule(assignment.visibility_rule));
    } else {
      setRoleId(undefined);
      setIndefinite(true);
      setValidFrom(null);
      setValidTo(null);
      setVisibility({});
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, assignment?.uid]);

  const selectedRole = roles.find((r) => r.pk === roleId);
  const handleVisibilityChange = (next: VisibilityState) => {
    setVisibility(next);
    setVisibilityTouched(true);
  };
  const onError = (err: unknown) => {
    const result = parseAssignmentError(err, validationT.t);
    if (!hasAnyMessage(result)) {
      message.error(commonT.t('error400'));
      return;
    }
    setParsed(result);
    if (result.isSelfForbidden) {
      message.error(t('revoke.selfForbidden'));
    }
  };
  const onSuccess = () => {
    message.success(t('modal.success'));
    onClose();
  };
  const handleSave = () => {
    if (!roleId) {
      return;
    }
    setParsed(EMPTY_PARSED);
    const from = indefinite ? null : validFrom;
    const to = indefinite ? null : validTo;
    if (isEdit && assignment) {
      // Строгая схема PUT: кладём только реально изменённые поля.
      // Отсутствие поля = «не трогать», явный null (срок/правило) = «снять».
      const data: UpdateAssignmentSchema = {};
      if (roleId !== assignment.role_id) {
        data.role_id = roleId;
      }
      if (from !== (assignment.valid_from ?? null)) {
        data.valid_from = from;
      }
      if (to !== (assignment.valid_to ?? null)) {
        data.valid_to = to;
      }
      if (visibilityTouched) {
        data.visibility_rule = buildVisibilityRule(visibility);
      }
      // Ничего не менялось — запрос не нужен.
      if (Object.keys(data).length === 0) {
        onClose();
        return;
      }
      update.mutate({ uid: assignment.uid, data }, { onSuccess, onError });
    } else {
      const data: CreateAssignmentSchema = {
        user_id: employee.pk,
        role_id: roleId,
        valid_from: from,
        valid_to: to,
        visibility_rule: buildVisibilityRule(visibility),
      };
      create.mutate(data, { onSuccess, onError });
    }
  };
  const dateError = parsed.fieldErrors.valid_to || parsed.fieldErrors.valid_from;
  return (
    <Modal
      open={open}
      onCancel={onClose}
      onOk={handleSave}
      okButtonProps={{ disabled: !roleId }}
      confirmLoading={submitting}
      width={760}
      okText={t('modal.save')}
      cancelText={t('modal.cancel')}
      title={
        <Space size={8} align="baseline">
          <Typography.Text strong>{isEdit ? t('modal.editTitle') : t('modal.newTitle')}</Typography.Text>
          <Typography.Text type="secondary">
            {employee.first_name} {employee.last_name}
          </Typography.Text>
        </Space>
      }>
      <Space direction="vertical" size={16} style={{ width: '100%', paddingTop: 8 }}>
        {/* Роль */}
        <div>
          <Typography.Text>
            <span style={{ color: '#ff4d4f', marginRight: 4 }}>*</span>
            {t('modal.roleLabel')}
          </Typography.Text>
          <Select
            style={{ width: '100%', marginTop: 4 }}
            loading={rolesLoading}
            showSearch
            optionFilterProp="label"
            value={roleId}
            placeholder={t('modal.rolePlaceholder')}
            onChange={(v) => setRoleId(v)}
            options={roles
              .filter((r) => r.code !== BASELINE_ROLE_CODE || r.pk === assignment?.role_id)
              .map((r) => ({ label: r.name, value: r.pk, moduleCaption: roleModuleCaption(r) }))}
            optionRender={(option) =>
              option.data.moduleCaption ? (
                <Flex vertical style={{ opacity: 0.6 }}>
                  <span>{option.label}</span>
                  <Typography.Text type="secondary" style={{ fontSize: 12 }}>
                    {option.data.moduleCaption}
                  </Typography.Text>
                </Flex>
              ) : (
                option.label
              )
            }
          />
          {parsed.fieldErrors.role_id && (
            <Typography.Text type="danger" style={{ fontSize: 12 }}>
              {parsed.fieldErrors.role_id}
            </Typography.Text>
          )}
        </div>
        {/* Что даёт роль */}
        {selectedRole && <RolePermissionsPreview permissions={selectedRole.permissions} />}
        {/* Срок действия */}
        <div>
          <Typography.Text>{t('modal.termLabel')}</Typography.Text>
          <div style={{ marginTop: 4 }}>
            <Checkbox checked={indefinite} onChange={(e) => setIndefinite(e.target.checked)}>
              {t('modal.indefinite')}
            </Checkbox>
          </div>
          {!indefinite && (
            <Space size={12} style={{ marginTop: 8 }} wrap>
              <span>
                {t('modal.from')}{' '}
                <DatePicker
                  value={validFrom ? dayjs(validFrom) : null}
                  onChange={(d) => setValidFrom(d ? d.toISOString() : null)}
                  format="DD.MM.YYYY"
                />
              </span>
              <span>
                {t('modal.to')}{' '}
                <DatePicker
                  value={validTo ? dayjs(validTo) : null}
                  onChange={(d) => setValidTo(d ? d.toISOString() : null)}
                  format="DD.MM.YYYY"
                  status={dateError ? 'error' : undefined}
                />
              </span>
            </Space>
          )}
          {dateError && (
            <div>
              <Typography.Text type="danger" style={{ fontSize: 12 }}>
                {dateError}
              </Typography.Text>
            </div>
          )}
        </div>
        {/* Область видимости */}
        <div>
          <Typography.Text strong>{t('visibility.title')}</Typography.Text>
          <div style={{ marginTop: 8 }}>
            {!roleId ? (
              <Typography.Text type="secondary" italic>
                {t('visibility.pickRoleFirst')}
              </Typography.Text>
            ) : (
              <VisibilityRuleBuilder
                attributes={ruleAttributes}
                permissions={selectedRole?.permissions}
                state={visibility}
                onChange={handleVisibilityChange}
              />
            )}
          </div>
        </div>
        {(parsed.ruleErrors.length > 0 || parsed.generalErrors.length > 0) && (
          <Alert
            type="error"
            showIcon
            message={[...parsed.generalErrors, ...parsed.ruleErrors].map((m, i) => (
              <div key={i}>{m}</div>
            ))}
          />
        )}
      </Space>
    </Modal>
  );
};
